import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiDateFilterBar, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface ReferralContact {
  customer_user_id: number
  contact_name: string
  customer_id: number | null
  customer_name: string | null
  referral_count: number
  total_billable: number
}

interface Props {
  contacts: ReferralContact[]
  start_date: string
  end_date: string
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return "-"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function drillDownUrl(
  row: ReferralContact,
  startDate: string,
  endDate: string,
): string {
  const params = new URLSearchParams({
    customer_id: String(row.customer_id ?? ""),
    contact_id: String(row.customer_user_id),
    start_date: startDate,
    end_date: endDate,
  })
  return `/reports/referrals?${params.toString()}`
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 25

const buildColumns = (
  startDate: string,
  endDate: string,
): HpiColumn<ReferralContact>[] => [
  {
    header: "Contact",
    render: (row) => (
      <Link
        href={drillDownUrl(row, startDate, endDate)}
        className="text-sm font-medium text-blue-600 hover:underline"
      >
        {row.contact_name}
      </Link>
    ),
  },
  {
    header: "Customer",
    render: (row) => (
      <Link
        href={drillDownUrl(row, startDate, endDate)}
        className="text-sm text-blue-600 hover:underline"
      >
        {row.customer_name ?? "-"}
      </Link>
    ),
  },
  {
    header: "#",
    width: "max-w-[60px]",
    align: "right",
    render: (row) => (
      <Link
        href={drillDownUrl(row, startDate, endDate)}
        className="text-sm text-blue-600 hover:underline"
      >
        {row.referral_count}
      </Link>
    ),
  },
  {
    header: "Total",
    subHeader: "Billable",
    width: "max-w-[140px]",
    align: "right",
    render: (row) => (
      <Link
        href={drillDownUrl(row, startDate, endDate)}
        className="text-sm text-blue-600 hover:underline"
      >
        {formatCurrency(row.total_billable)}
      </Link>
    ),
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/referrals_by_contact" },
  { title: "Referrals by Contact", href: "/reports/referrals_by_contact" },
]

export default function ReferralsByContactReport({
  contacts,
  start_date,
  end_date,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(contacts.length / PER_PAGE)
  const paginated = contacts.slice((page - 1) * PER_PAGE, page * PER_PAGE)
  const columns = buildColumns(startDate, endDate)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/referrals_by_contact",
      {
        start_date: startDate,
        end_date: endDate,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Top Referrals by Contact" />

      <div className="p-6">
        {/* Date Filter Bar */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        {/* Data Table */}
        <HpiDataTable<ReferralContact>
          title="Top Referrals by Contact"
          columns={columns}
          data={paginated}
          keyFn={(row) => row.customer_user_id}
          emptyMessage="No referral contacts found for the selected date range."
          pagination={
            contacts.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={contacts.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />
      </div>
    </AppLayout>
  )
}
